import { Search } from "@mui/icons-material";
import { Box, Button, FormControl, IconButton, MenuItem, Paper, Select, TextField, Typography } from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import { PaginationModel } from "utils/PaginationType";
import { CustomerTableColumns } from "./CustomerModel";
import { Customer } from "./CustomerType";
import ShireTable from "components/ShireTable";
import { getCustomers } from "services/CustomerService";
import { CustomerRank } from "utils/constant";
import CustomerAdjustmentPointModal from "./CustomerAdjustmentPointModal";
import CustomerSendVoucherModal from "./CustomerSendVoucherModal";

const CreateCustomerModal = lazy(() => import('./CreateCustomerModal'));

export type SearchCustomerParams = {
  search: string;
  rank: string;
}

export const CustomerManagement = () => {
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 10,
    rowCount: 0,
  });
  const [customers, setCustomers] = useState<any[]>([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<Customer>({ ...initialValuesDefault });
  const [isEdit, setIsEdit] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchParams, setSearchParams] = useState<SearchCustomerParams>({
    search: '',
    rank: '',
  });
  const [openAdjustmentPoint, setOpenAdjustmentPoint] = useState(false);
  const [customerAdjustmentPoint, setCustomerAdjustmentPoint] = useState<any>({ ...initialValuesDefault });
  const [openSendVoucher, setOpenSendVoucher] = useState(false);
  const [customerIdsSendVoucher, setCustomerIdsSendVoucher] = useState<number[]>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<any[]>([]);

  useEffect(() => {
    fetchCustomers(paginationModel.page + 1, paginationModel.pageSize, searchParams);
  }, [paginationModel.page, paginationModel.pageSize, searchParams]);

  const fetchCustomers = async (page: number, pageSize: number, searchParams: SearchCustomerParams) => {
    const response = await getCustomers(page, pageSize, searchParams);
    setCustomers(response.records);
    setTotalCustomers(response.total);
  };

  const handleAdd = () => {
    setOpen(true);
    setIsEdit(false);
    setInitialValues({ ...initialValuesDefault });
  };

  const handleEdit = (customer: Customer) => {
    setOpen(true);
    setIsEdit(true);
    setInitialValues(customer);
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, search: searchValue }));
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAdjustmentPoint = (customer: any) => {
    setOpenAdjustmentPoint(true);
    setCustomerAdjustmentPoint(customer);
  };

  const handleSendVoucher = (customer: Customer) => {
    setOpenSendVoucher(true);
    setCustomerIdsSendVoucher([customer.id]);
  };

  const handleSendVoucherInBulk = () => {
    setOpenSendVoucher(true);
    setCustomerIdsSendVoucher(selectedCustomerIds.map((customer: Customer) => customer.id));
  };

  const handleSelectionChange = (selectedRows: any[]) => {
    setSelectedCustomerIds(selectedRows);
  };

  return <>
    <Typography variant="h4" sx={{ marginBottom: 3 }}>Quản lý khách hàng</Typography>
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <FormControl sx={{ flex: 1, position: 'relative' }}>
              <TextField
                id="search"
                name="search"
                placeholder="Nhập tên khách hàng"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyUp={onKeyUp}
              />
            </FormControl>
            <IconButton
              color="primary"
              onClick={handleSearch}
              disabled={searchParams.search === ''}
              sx={{ position: 'absolute', right: 0 }}
            >
              <Search />
            </IconButton>
          </Box>
          <FormControl>
            <Select
              id="rank"
              name="rank"
              value={searchParams.rank}
              onChange={(e) => setSearchParams(prev => ({ ...prev, rank: e.target.value.toString() }))}
              displayEmpty
            >
              <MenuItem value="">
                <em>Tất cả hạng</em>
              </MenuItem>
              {Object.keys(CustomerRank).map((rank) => (
                <MenuItem key={rank} value={rank}>{rank}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedCustomerIds.length > 0 && (
            <Button variant="contained" color="primary" onClick={handleSendVoucherInBulk}>
              Gửi voucher
            </Button>
          )}
        </Box>

        <Button variant="contained" color="primary" onClick={handleAdd}>
          Thêm mới khách hàng
        </Button>
      </Box>
      <ShireTable
        columns={CustomerTableColumns(fetchCustomers, paginationModel, handleEdit, handleAdjustmentPoint, handleSendVoucher)}
        rows={customers}
        paginationModel={{
          ...paginationModel,
          rowCount: totalCustomers,
        }}
        onPaginationModelChange={setPaginationModel}
        loading={false}
        onSelectionChange={handleSelectionChange}
      />
    </Paper>
    <Suspense fallback={<div>Loading...</div>}>
      <CreateCustomerModal
        open={open}
        onClose={() => setOpen(false)}
        isEdit={isEdit}
        initialValues={initialValues}
        reloadTableData={() => fetchCustomers(paginationModel.page + 1, paginationModel.pageSize, searchParams)}
      />
    </Suspense>
    <Suspense fallback={<div>Loading...</div>}>
      <CustomerAdjustmentPointModal
        open={openAdjustmentPoint}
        onClose={() => setOpenAdjustmentPoint(false)}
        customerId={customerAdjustmentPoint.id}
        currentPoint={customerAdjustmentPoint.currentPoint}
        accumulatedPoint={customerAdjustmentPoint.accumulatedPoint}
        reloadTableData={() => fetchCustomers(paginationModel.page + 1, paginationModel.pageSize, searchParams)}
      />
    </Suspense>
    <Suspense fallback={<div>Loading...</div>}>
      <CustomerSendVoucherModal
        open={openSendVoucher}
        onClose={() => setOpenSendVoucher(false)}
        initialValues={customerIdsSendVoucher}
        reloadTableData={() => fetchCustomers(paginationModel.page + 1, paginationModel.pageSize, searchParams)}
      />
    </Suspense>
  </>;
};

const initialValuesDefault: Customer = {
  id: 0,
  name: '',
  phone: '',
  point: 0
};